/** How good is this position, in one number and the terms that built it.
 *
 * Everything is denominated in gold-equivalents, because gold is the one unit
 * the content already prices every other resource against
 * (`content.resourceValue`) and because a number a designer can read - "that
 * town is worth about twelve thousand" - survives contact with a balance sweep
 * in a way that an abstract score does not.
 *
 * The function is deliberately shallow: it scores what is *there*, never what
 * might happen. Anything that needs a prediction runs the real battle kernel
 * (`simulate.ts`); nothing here guesses at an outcome.
 */

import type {
  ArmyState, ContentRegistry, ObservedHero, ObservedTown, PlayerObservation,
  ResourceKind, Resources,
} from "@game/core";
import type { Shadow } from "./shadow.js";

export interface EvalTerm {
  readonly source: string;
  readonly value: number;
}

export interface Evaluation {
  readonly value: number;
  readonly terms: readonly EvalTerm[];
}

// ---- weights. Gold-equivalents, all integers, all in one place on purpose.

/** A town with nothing built is still a recruiting seat and a victory token. */
export const TOWN_BASE = 12000;
export const BUILDING_VALUE = 900;
/** Creatures waiting in a dwelling are gold already spent, discounted for risk. */
export const AVAILABLE_DISCOUNT_BP = 2500;
/** Days of income a captured mine is worth on the day it is taken. */
export const MINE_HORIZON_DAYS = 25;
/** A mine whose content definition is missing still earns something. */
export const MINE_FALLBACK = 8000;
export const OWNED_DWELLING_VALUE = 3000;
export const HERO_LEVEL_VALUE = 700;
export const HERO_MIGHT_STAT_VALUE = 200;
export const HERO_MAGIC_STAT_VALUE = 150;
export const HERO_SPELL_VALUE = 100;
/** One explored cell. Small, but map control is the thing that compounds. */
export const EXPLORED_CELL_VALUE = 3;

/**
 * Tier weighting on top of `aiValue`. Two thousand spearwardens and forty angels can
 * price the same and are not the same army: the angels take one slot, move at
 * their own speed, and cannot be ground down by attrition. Four percent a tier
 * is small enough not to swamp `aiValue` and large enough to break ties toward
 * the better army.
 */
export const TIER_WEIGHT_BP_PER_TIER = 400;

export function armyStrength(content: ContentRegistry, a: ArmyState | null): number {
  if (a === null) return 0;
  let v = 0;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = content.creatures.get(s.def);
    if (def === undefined) continue;
    const w = 10000 + TIER_WEIGHT_BP_PER_TIER * def.tier;
    v += Math.floor((def.aiValue * s.count * w) / 10000);
  }
  return v;
}

/** Plain `aiValue` times count: the unit the battle scorer trades in. */
export function rawArmyValue(content: ContentRegistry, a: ArmyState | null): number {
  if (a === null) return 0;
  let v = 0;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    v += (content.creatures.get(s.def)?.aiValue ?? 0) * s.count;
  }
  return v;
}

export function goldEquivalent(content: ContentRegistry, r: Partial<Resources>): number {
  let v = 0;
  for (const k of Object.keys(r).sort() as ResourceKind[]) {
    const n = r[k] ?? 0;
    if (n === 0) continue;
    v += n * (content.resourceValue[k] ?? 1);
  }
  return v;
}

function heroValue(h: ObservedHero): number {
  const d = h.detail;
  if (d === null) return HERO_LEVEL_VALUE * h.level;
  const primary = h.primary ?? d.primary;
  let v = HERO_LEVEL_VALUE * d.level;
  v += (primary.attack + primary.defense) * HERO_MIGHT_STAT_VALUE;
  v += (primary.spellPower + primary.knowledge) * HERO_MAGIC_STAT_VALUE;
  v += d.spellbook.length * HERO_SPELL_VALUE;
  return v;
}

function artifactValue(content: ContentRegistry, h: ObservedHero): number {
  const d = h.detail;
  if (d === null) return 0;
  let v = 0;
  for (const slot of Object.keys(d.artifacts).sort()) {
    const id = d.artifacts[slot];
    if (id == null) continue;
    v += content.artifacts.get(id)?.cost ?? 0;
  }
  for (const id of d.backpack) v += content.artifacts.get(id)?.cost ?? 0;
  return v;
}

function townValue(sh: Shadow, t: ObservedTown): number {
  const d = t.detail;
  if (d === null) return TOWN_BASE;
  let v = TOWN_BASE + d.built.length * BUILDING_VALUE;
  for (const key of Object.keys(d.available).sort()) {
    const count = d.available[key] ?? 0;
    if (count <= 0) continue;
    const def = sh.content.creatures.get(key as never);
    if (def === undefined) continue;
    v += Math.floor((def.aiValue * count * AVAILABLE_DISCOUNT_BP) / 10000);
  }
  return v;
}

/** Daily gold-equivalent of one owned map object, from its content definition. */
export function mineIncome(content: ContentRegistry, def: string): number {
  const d = content.mapObjects.get(def);
  if (d === undefined) return 0;
  const res = d.payload["resource"];
  const amount = d.payload["amount"];
  if (typeof res !== "string" || typeof amount !== "number") return 0;
  return amount * (content.resourceValue[res as ResourceKind] ?? 1);
}

function exploredCells(obs: PlayerObservation): number {
  const v = obs.map.visibility;
  let n = 0;
  for (let i = 0; i < v.length; i++) if (v[i] !== 0) n++;
  return n;
}

/**
 * The one evaluation function. Every planner that needs "is this better" calls
 * this, so a change to a weight moves the whole AI at once instead of moving
 * whichever subsystem happened to hard-code it.
 */
export function evaluatePosition(sh: Shadow): Evaluation {
  const { obs, content } = sh;
  const terms: EvalTerm[] = [];
  const add = (source: string, value: number): void => {
    if (value !== 0) terms.push({ source, value });
  };

  let army = 0;
  let heroes = 0;
  let artifacts = 0;
  for (const h of obs.heroes) {
    if (h.owner !== obs.viewer) continue;
    army += armyStrength(content, h.army);
    heroes += heroValue(h);
    artifacts += artifactValue(content, h);
  }

  let towns = 0;
  let garrisons = 0;
  for (const t of obs.towns) {
    if (t.detail === null) continue;
    towns += townValue(sh, t);
    garrisons += armyStrength(content, t.garrison);
  }

  let mines = 0;
  for (const o of sh.objects) {
    if (o.owner !== obs.viewer) continue;
    if (o.kind === "mine") {
      const daily = mineIncome(content, o.def);
      mines += daily > 0 ? daily * MINE_HORIZON_DAYS : MINE_FALLBACK;
    } else if (o.kind === "dwelling") {
      mines += OWNED_DWELLING_VALUE;
    }
  }

  const resources = obs.resources === null ? 0 : goldEquivalent(content, obs.resources);
  const control = exploredCells(obs) * EXPLORED_CELL_VALUE;

  add("army", army);
  add("garrison", garrisons);
  add("towns", towns);
  add("mines", mines);
  add("heroes", heroes);
  add("artifacts", artifacts);
  add("resources", resources);
  add("control", control);

  let value = 0;
  for (const t of terms) value += t.value;
  return { value, terms };
}

/**
 * Strength of everything one player can put in a fight, for the "do I win this"
 * comparison. Kept apart from `evaluatePosition` because a town is worth a lot
 * and cannot swing a sword.
 */
export function fieldStrength(sh: Shadow): number {
  let v = 0;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.obs.viewer) continue;
    v += armyStrength(sh.content, h.army);
  }
  return v;
}

/** The strongest single stack-carrying hero the viewer owns, by army strength. */
export function strongestOwnArmy(sh: Shadow): number {
  let best = 0;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.obs.viewer) continue;
    const v = armyStrength(sh.content, h.army);
    if (v > best) best = v;
  }
  return best;
}
